import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { LayoutChangeEvent, Platform, useWindowDimensions } from 'react-native'
import { useSharedValue, withSpring } from 'react-native-reanimated'

import { colors } from '@ui/colors'
import Icon from '@ui/elements/Icon'
import Typography from '@ui/typography'
import { View } from 'react-native'
import cn from '@ui/utils/cn'
import * as DropdownMenu from 'zeego/dropdown-menu'
import { SelectButton, SelectSuffix } from './SelectButton'
import { SelectProps } from './SelectProps'

const sContainer = 'translate-x-[0px] translate-y-[1px]'
const borderRadius = 16
const heightItem = 48

const isWeb = Platform.OS === 'web'

function Select<T>({
  options,
  onChange,
  value,
  disabled,
  isLoading,
  mark = 'dot',
  clearValue,
  hiddenChevron,
  textEnd,
  ...props
}: SelectProps<T>) {
  const active = useSharedValue(0)
  const ref = useRef<View>(null)
  const [containerWidth, setContainerWidth] = useState(200)
  const window = useWindowDimensions()
  const isEmpty = !options.length

  const [suffixWidth, setSuffixWidth] = useState(0)

  // TODO: onOpenChange is supported only on web for current version of zeego
  // We need to bump to v3
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      active.set(
        withSpring(isOpen ? 1 : 0, {
          damping: 10,
          stiffness: 150,
          overshootClamping: true,
        })
      )
    },
    [active]
  )

  useLayoutEffect(() => {
    if (ref?.current) {
      ref.current.measure((_x, _y, width) => {
        setContainerWidth(width)
      })
    }
  }, [])

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value]
  )

  const isClearable =
    typeof clearValue !== 'undefined' && clearValue !== value && !!value

  const onClear = useCallback(() => {
    // Make ts happy
    if (!isClearable) return
    onChange(clearValue)
  }, [isClearable, onChange, clearValue])

  const onSuffixLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const { width } = e.nativeEvent.layout
      setSuffixWidth(width)
    },
    [setSuffixWidth]
  )

  return (
    <View ref={ref} className="relative" style={props.styleOuter}>
      <DropdownMenu.Root onOpenChange={handleOpenChange} defaultOpen={false}>
        <DropdownMenu.Trigger
          disabled={disabled || isEmpty}
          className="rounded-lg outline-none focus:outline-none"
        >
          <SelectButton
            active={active}
            value={selected?.label}
            disabled={disabled || isEmpty}
            suffixWidth={suffixWidth}
            // isLoading={isLoading}
            isEmpty={isEmpty}
            {...props}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          style={{
            borderRadius,
            width: containerWidth,
            maxHeight: Math.floor(window.height / 2 / heightItem) * heightItem,
            // maxHeight: 6 * 48
          }}
          className={cn(
            sContainer,
            'w-full overflow-y-scroll border border-black/10 bg-white/95 p-0'
          )}
        >
          {options.map((option, index) => {
            const last = index === options.length - 1
            const selected = option.value === value
            return (
              <DropdownMenu.Item
                onSelect={() => onChange(option.value)}
                key={String(option.value)}
                className={cn(
                  'h-12 w-full cursor-pointer items-center gap-2 border-black/10',
                  !last && 'border-b',
                  props.classNameOption,
                  'hover:border-black/10 hover:bg-gray-75 hover:outline-none focus:-outline-offset-2'
                )}
              >
                {/* // WARN: this component requires string child explicitly defined. Conditioned children will throw on iOS   */}
                <DropdownMenu.ItemTitle
                  className={cn(
                    'flex h-12 w-full items-center border-black/10 px-6',
                    props.center && 'flex-col items-center justify-center px-0',
                    mark === 'dot' &&
                      'flex-row items-center justify-start gap-2 px-4'
                  )}
                  style={{ height: heightItem }}
                >
                  {isWeb ? (
                    <>
                      {mark === 'dot' && (
                        <View className="h-6 w-6 items-center justify-center">
                          <Icon
                            name={selected ? 'circle-filled' : 'circle'}
                            color={selected ? colors.text : colors.text50}
                            size={12}
                          />
                        </View>
                      )}
                      <Typography
                        variant="SM"
                        className={cn(
                          'px-1.5 text-left font-text-semibold',
                          selected ? 'color-text' : 'color-desc',
                          props.center &&
                            'flex items-center justify-center px-0 text-center',
                          mark === 'underline' && 'flex'
                        )}
                      >
                        {option.label}
                        {mark === 'underline' && (
                          <View
                            className={cn(
                              'absolute w-full bg-text',
                              selected ? 'opacity-100' : 'opacity-0'
                            )}
                            style={{ height: 2, bottom: 1 }}
                          />
                        )}
                      </Typography>
                    </>
                  ) : (
                    option.label
                  )}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      <View
        onLayout={onSuffixLayout}
        pointerEvents="box-none"
        className={cn(
          'absolute right-1 flex flex-1 flex-row items-center gap-1',
          props.labelVariant === 'top' && 'top-9'
        )}
      >
        <SelectSuffix
          isLoading={isLoading}
          textEnd={textEnd}
          disabled={disabled || isEmpty}
          hiddenChevron={hiddenChevron}
          active={active}
          error={!!props.errorMessage}
          onClear={isClearable ? onClear : undefined}
        />
      </View>
    </View>
  )
}

export default Select
